import React, { useState, useEffect, useRef } from "react";
import ProvidersList from "./ProvidersList";
import defaultAvatar from "../../assets/avatar.png";
import { useApp } from "../../context/AppContext";
import { API_ENDPOINTS, API_BASE_URL } from "../../config";
import {
  Message,
  ChatResponse,
  SendMessageRequest,
  Provider,
} from "../../types";
import { TTSService } from "../../services/TTSService";

// Web Speech API type
interface SpeechRecognitionResult {
  transcript: string;
  confidence: number;
  isFinal: boolean;
}

interface SpeechRecognitionResults extends Array<SpeechRecognitionResult[]> {
  readonly length: number;
  [index: number]: SpeechRecognitionResult[];
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResults;
  resultIndex: number;
}

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

type SpeechRecognitionType = any;

interface ChatBoxProps {
  initialMessage: string;
  existingMessages?: Message[];
  existingProviders?: Provider[];
  onConversationIdChange?: (conversationId: string) => void;
}

// Format message content with line breaks and markdown-like formatting
const formatMessageContent = (content: string): string => {
  if (!content) return '';
  
  // Convert line breaks
  let formatted = content.replace(/\n/g, '<br/>');
  
  // Convert **bold** to <strong>
  formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  
  // Convert markdown links [text](url) to HTML links
  formatted = formatted.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s\)]+)\)/gi,
    '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline;">$1</a>'
  );
  
  // Convert plain URLs (Profile: URL or standalone) to clickable links
  formatted = formatted.replace(
    /(?:Profile:\s*)?(https?:\/\/[^\s<\)]+)(?![^<]*<\/a>)/gi,
    (match, url) => {
      // Don't replace if already inside an anchor tag
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline;">${url}</a>`;
    }
  );
  
  return formatted;
};

const ChatBox: React.FC<ChatBoxProps> = ({
  initialMessage,
  existingMessages,
  existingProviders,
  onConversationIdChange,
}) => {
  const { state, toggleTTS } = useApp();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [providers, setProviders] = useState<Provider[]>([]);
  const [showProviders, setShowProviders] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [recognition, setRecognition] = useState<SpeechRecognitionType | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialMessageSent = useRef(false);
  const messageCounterRef = useRef(0); // Counter for generating unique message IDs

  useEffect(() => {
    if ("SpeechRecognition" in window || "webkitSpeechRecognition" in window) {
      const SpeechRecognitionAPI =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognitionAPI();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = "en-US"; // Set speech recognition language to English

      recognitionInstance.onresult = (event: SpeechRecognitionEvent) => {
        const results = Array.from(
          event.results
        ) as SpeechRecognitionResult[][];
        const transcript = results
          .map((result) => result[0])
          .map((result) => result.transcript)
          .join("");
        setNewMessage(transcript);
      };

      recognitionInstance.onerror = (event: any) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
      };

      setRecognition(recognitionInstance);
    }
  }, []);

  const toggleListening = () => {
    if (!recognition) {
      alert("Your browser does not support speech recognition");
      return;
    }

    if (isListening) {
      recognition.stop();
    } else {
      recognition.start();
      setIsListening(true);
    }
  };

  // Get conversation ID
  const getConversationId = (): string => {
    return sessionStorage.getItem("conversationId") || "";
  };

  // Set conversation ID
  const setConversationId = (id: string) => {
    if (id) {
      sessionStorage.setItem("conversationId", id);
    } else {
      sessionStorage.removeItem("conversationId");
    }
    if (onConversationIdChange) {
      onConversationIdChange(id);
    }
  };

  // Clear conversation ID
  const clearConversationId = () => {
    setConversationId("");
    setMessages([]);
  };

  // Load history messages
  useEffect(() => {
    console.log("ChatBox received existingMessages:", existingMessages);
    if (existingMessages && existingMessages.length > 0) {
      console.log("Setting messages in ChatBox:", existingMessages);
      setMessages(existingMessages);
    }
  }, [existingMessages]);

  // Load existing providers (restore recommendations panel)
  useEffect(() => {
    console.log("ChatBox received existingProviders:", existingProviders);
    if (existingProviders && existingProviders.length > 0) {
      console.log("Restoring providers panel with", existingProviders.length, "providers");
      setProviders(existingProviders);
      setShowProviders(true);
    }
  }, [existingProviders]);

  useEffect(() => {
    if (initialMessage && !initialMessageSent.current) {
      initialMessageSent.current = true;
      handleSendMessage(initialMessage);
    }
  }, [initialMessage]);

  // Create a new conversation
  const createConversation = async (message: string): Promise<string> => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    console.log("Creating new conversation with message:", message);
    console.log("Using endpoint:", API_ENDPOINTS.CONVERSATION);

    try {
      const response = await fetch(API_ENDPOINTS.CONVERSATION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: message,
        }),
      });

      console.log("Create conversation response status:", response.status);
      console.log("Create conversation response ok:", response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || `Failed to create conversation (HTTP ${response.status})`;
        console.error("Create conversation error:", errorMsg, errorData);
        throw new Error(errorMsg);
      }

      const data = await response.json();
      console.log("Conversation created successfully:", data);
      return data.conversation_id;
    } catch (error) {
      console.error("Error creating conversation:", error);
      throw error;
    }
  };

  // Send chat message
  const sendChatMessage = async (
    conversationId: string,
    question: string
  ): Promise<ChatResponse> => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    console.log("API Configuration Debug:");
    console.log("API_BASE_URL:", API_BASE_URL);
    console.log("CHAT endpoint:", API_ENDPOINTS.CHAT);
    console.log("Making request to:", API_ENDPOINTS.CHAT);

    try {
      const response = await fetch(API_ENDPOINTS.CHAT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          question: question,
        }),
      });
      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);
      console.log("Response ok:", response.ok);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || `HTTP ${response.status}: Failed to send message`;
        console.error("API Error:", errorMsg, errorData);
        throw new Error(errorMsg);
      }

      const responseData = await response.json();
      console.log("Response data received:", responseData);
      return responseData;
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (content: string = newMessage) => {
    if (!content.trim()) {
      return;
    }

    try {
      const userMessage: Message = {
        id: `msg-${getConversationId() || 'new'}-${messageCounterRef.current++}`,
        content: content,
        isUser: true,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setNewMessage("");

      let currentConversationId = getConversationId();
      // If no conversationId, create a new conversation first
      if (!currentConversationId) {
        currentConversationId = await createConversation(content);
        console.log("New conversation created:", currentConversationId);
        setConversationId(currentConversationId);
      }

      console.log("Using conversation_id:", currentConversationId);
      // Send chat message
      const response = await sendChatMessage(currentConversationId, content);
      console.log("Chat response:", response);
      console.log("Response user_list:", response.user_list);
      console.log("Response user_list length:", response.user_list?.length);

      const aiMessage: Message = {
        id: `msg-${currentConversationId}-${messageCounterRef.current++}`,
        content: response.answer,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);

      // Trigger TTS if enabled (run in background to avoid blocking UI)
      if (state.ttsEnabled && response.answer) {
        // Run TTS in background without awaiting
        (async () => {
          try {
            console.log('TTS: Attempting to speak:', response.answer.substring(0, 50) + '...');
            setIsSpeaking(true);

            const ttsService = TTSService.getInstance();
            console.log('TTS: Service enabled?', ttsService.isEnabled());
            console.log('TTS: Current engine:', ttsService.getCurrentEngineInfo().name);

            // Stop any currently playing TTS
            ttsService.stop();

            // Get selected voice from localStorage (for Kokoro engine)
            const selectedVoice = localStorage.getItem('tts-voice-kokoro');

            // Speak the new response with selected voice
            await ttsService.speak(response.answer, selectedVoice ? { voice: selectedVoice } : undefined);
            console.log('TTS: Speech completed');
          } catch (error) {
            console.error('TTS playback failed:', error);
            // Don't break chat if TTS fails
          } finally {
            setIsSpeaking(false);
          }
        })();
      } else {
        console.log('TTS: Not enabled or no response text');
      }

      if (response.user_list && response.user_list.length > 0) {
        setProviders(response.user_list);
        setShowProviders(true);
        // Remove setCanAskMore(false) to keep chat active
      } else if (response.user_list && response.user_list.length === 0) {
        // Only clear panel if explicitly provided empty array
        setProviders([]);
        setShowProviders(false);
      }
      // If no user_list in response, keep existing panel visible (persistent behavior)
    } catch (error: any) {
      console.error("Failed to send message:", error);

      // Parse error message to provide user-friendly feedback
      let userFriendlyError = "Failed to send message. Please try again.";

      if (error.message) {
        const errorStr = error.message.toLowerCase();

        // Check for rate limit error
        if (errorStr.includes("rate limit") || errorStr.includes("429")) {
          userFriendlyError = "⚠️ API rate limit reached. Please wait a moment and try again in 30 seconds.";
        }
        // Check for API errors
        else if (errorStr.includes("llm api error")) {
          userFriendlyError = "⚠️ AI service is temporarily unavailable. Please try again in a moment.";
        }
        // Check for network errors
        else if (errorStr.includes("fetch") || errorStr.includes("network")) {
          userFriendlyError = "⚠️ Network error. Please check your connection and try again.";
        }
      }

      // Display error message to user
      setErrorMessage(userFriendlyError);

      // Auto-clear error after 10 seconds
      setTimeout(() => setErrorMessage(null), 10000);
    }
  };

  const handleCloseProviders = () => {
    setShowProviders(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div
      className={`chatbox-container ${showProviders ? "with-providers" : ""}`}
    >
      <div className="chatbox">
        {errorMessage && (
          <div
            style={{
              backgroundColor: "#fee",
              border: "1px solid #fcc",
              borderRadius: "8px",
              padding: "12px",
              margin: "10px",
              color: "#c33",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>{errorMessage}</span>
            <button
              onClick={() => setErrorMessage(null)}
              style={{
                background: "none",
                border: "none",
                color: "#c33",
                cursor: "pointer",
                fontSize: "18px",
                fontWeight: "bold",
                padding: "0 8px",
              }}
            >
              ×
            </button>
          </div>
        )}
        <div className="messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${
                message.isUser ? "user-message" : "ai-message"
              }`}
            >
              <div 
                className="message-bubble"
                dangerouslySetInnerHTML={{ 
                  __html: formatMessageContent(message.content) 
                }}
              />
              <div className="message-info">
                <span>{message.timestamp.toLocaleTimeString()}</span>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        <form onSubmit={handleSubmit} className="message-input">
          <div className="input-container">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={
                showProviders 
                  ? "Refine your preferences or ask questions..." 
                  : "Type your message..."
              }
              className="chat-input"
              disabled={false}
              onKeyDown={handleKeyDown}
            />
            <button
              type="button"
              onClick={toggleListening}
              className={`voice-button ${isListening ? "listening" : ""}`}
              title={
                isListening
                  ? "Click to stop voice input"
                  : "Click to start voice input"
              }
              disabled={false}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M19 10v2a7 7 0 0 1-14 0v-2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 19v4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 23h8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={toggleTTS}
              className={`tts-button ${state.ttsEnabled ? "active" : ""} ${isSpeaking ? "speaking" : ""}`}
              title={
                isSpeaking
                  ? "Speaking... (click to stop)"
                  : state.ttsEnabled
                  ? "Disable voice responses"
                  : "Enable voice responses"
              }
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {state.ttsEnabled ? (
                  // Volume on icon
                  <path
                    d="M11 5L6 9H2v6h4l5 4V5z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ) : (
                  // Volume off icon
                  <path
                    d="M11 5L6 9H2v6h4l5 4V5z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
                <path
                  d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={isSpeaking ? "pulse" : ""}
                />
              </svg>
              {isSpeaking && (
                <span style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#22c55e',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }} />
              )}
            </button>
            <button
              type="submit"
              className="send-button"
              disabled={false}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22 2L11 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 2L15 22L11 13L2 9L22 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </form>
      </div>
      {showProviders && (
        <ProvidersList
          providers={providers}
          onClose={handleCloseProviders}
          conversationId={getConversationId()}
        />
      )}
    </div>
  );
};

export default ChatBox;
